import { Component, EventEmitter, Output, OnInit, OnDestroy, Input, AfterViewInit, inject } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { FeatureFlagsV2Service } from 'src/app/core/services/feature-flags.v2.service';
import { BipV2 } from 'src/app/core/models';

interface Section {
  key: string;
  label: string;
  selected: boolean;
  featureFlag?: string;
}

@Component({
  selector: 'app-pdf-section-selector',
  templateUrl: '././pdf-section-selector.component.html',
  styleUrls: ['./pdf-section-selector.component.scss']
})
export class PdfSectionSelectorComponent implements OnInit, OnDestroy, AfterViewInit {
  @Output() selectionConfirmed = new EventEmitter<string[]>(); // Emitirá las secciones seleccionadas
  @Output() errorOccurred = new EventEmitter<string>(); // Emitirá un error si no hay secciones seleccionadas
  @Input() bip: BipV2 | undefined;

  private destroy$ = new Subject<void>();

  sections: Section[] = [
    { key: 'background', label: 'Background Information', selected: true },
    { key: 'functionalImpairment', label: 'Functional Impairment', selected: true, featureFlag: 'insurance-statements-fields' },
    { key: 'individualizedRationale', label: 'Individualized Rationale', selected: true, featureFlag: 'insurance-statements-fields' },
    { key: 'treatmentAndResults', label: 'Previous Treatment and Results', selected: true },
    { key: 'schoolServicesJustification', label: 'School Services Justification', selected: true, featureFlag: 'insurance-statements-fields' },
    { key: 'maladaptives', label: 'Maladaptive Behaviors', selected: true },
    { key: 'assessmentsConducted', label: 'Assessment Conducted', selected: true },
    { key: 'prevalentSetting', label: 'Prevalent Setting Events & Antecedents', selected: true },
    { key: 'hypothesisBasedInterventions', label: 'Hypothesis-based Interventions', selected: true },
    { key: 'interventions', label: 'Interventions', selected: true },
    { key: 'maladaptiveGoals', label: 'Maladaptive Goals', selected: true },
    { key: 'replacementGoals', label: 'Replacement Goals', selected: true },
    { key: 'familyInvolvement', label: 'Family/Caregiver Training/Involvement', selected: true },
    { key: 'monitoringEvaluating', label: 'Monitoring and Evaluating the Effectiveness of the plan/ Treatment Fidelity', selected: true },
    { key: 'behaviorAssitant', label: 'Behavior Assistant Rationale', selected: true },
    { key: 'schoolFadingPlan', label: 'School Fading Plan', selected: true, featureFlag: 'insurance-statements-fields' },
    { key: 'scheduleOfServices', label: 'Schedule of Services', selected: true, featureFlag: 'insurance-statements-fields' },
    { key: 'generalizationTraining', label: 'Generalization Training', selected: true },
    { key: 'riskAssessment', label: 'Risk Assessment/Medical Necessity', selected: true },
    { key: 'fadingPlan', label: 'Fading Plan', selected: true },
    { key: 'dischargePlan', label: 'Discharge Plan', selected: true },
    { key: 'crisisPlan', label: 'Crisis Plan', selected: true },
    { key: 'deEscalationTechniques', label: 'De-escalation Techniques', selected: true },
    { key: 'serviceRecomendation', label: 'Service Recommendation', selected: true },
    { key: 'consentToTreatment', label: 'Consent to Treatment', selected: true },
    { key: 'policy', label: 'Policy', selected: true},
  ];

  // Filtered sections based on feature flags
  filteredSections: Section[] = [];
  allSelected = true; // Estado inicial:


  private featureFlagsService = inject(FeatureFlagsV2Service);

  ngOnInit(): void {
    // Subscribe to feature flags and filter sections accordingly
    this.featureFlagsService.getAllFlags$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((flags) => {
        this.filterSectionsByFeatureFlags(flags);
      });
    
    this.filterSections();
  }

  ngAfterViewInit(): void {
    const modalEl = document.getElementById('selectModal');
    if (modalEl) {
      modalEl.addEventListener('show.bs.modal', () => {
        this.filterSections();
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Filters sections based on feature flags
   * - Sections without featureFlag property are always included
   * - Sections with featureFlag are only included if the flag is enabled
   */
  private filterSectionsByFeatureFlags(flags: { [key: string]: boolean }): void {
    this.filteredSections = this.sections.filter((section) => {
      // If no feature flag is specified, always include the section
      if (!section.featureFlag) {
        return true;
      }
      // If feature flag is specified, only include if it's enabled
      return !!flags[section.featureFlag];
    });
  }

  /**
   * Filters sections related to school services based on bip.apply_schedule_of_services
   */
  private filterSectionsBySchoolServices(): void {
    if (this.bip ===  undefined || this.bip?.apply_schedule_of_services === undefined) {
      console.warn('Bip is undefined');
      return;
    }
    this.filteredSections = this.filteredSections.filter((section) => {
      if (
        section.key === 'schoolServicesJustification'
        || section.key === 'schoolFadingPlan' 
        || section.key === 'scheduleOfServices'
      ) {
        return !!this.bip?.apply_schedule_of_services;
      }
      return true;
    });
  }

  toggleSelection(): void {
    this.allSelected = !this.allSelected; // Cambiar el estado
    this.filteredSections.forEach((section) => (section.selected = this.allSelected)); // Seleccionar o deseleccionar todas
  }

  // Sincroniza el estado del botón Select/Deselect All cuando el usuario marca/desmarca individualmente
  onSectionChange(): void {
    this.allSelected = this.sections.every((s) => s.selected);
  }

  confirmSelection() {
    const selectedSections = this.filteredSections
      .filter((section) => section.selected)
      .map((section) => section.key);

      if (selectedSections.length === 0) {
        // Emitir un error si no hay secciones seleccionadas
        this.errorOccurred.emit('You must select at least one section.');
        console.log('Error emitted: No sections selected'); // Depuración
        return;
      }

      // El cierre del modal se gestiona desde el componente padre usando la API de Bootstrap.
      this.selectionConfirmed.emit(selectedSections);
  }

  private filterSections(): void {
    this.filterSectionsByFeatureFlags(this.featureFlagsService.getAllFlags());
    this.filterSectionsBySchoolServices();
  }
}
